import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import PageHeader, { type PageHeaderStat } from "../../../components/common/PageHeader";
import { getDashboardStatLabels } from "../utils/dashboardStatLabels";
import type { DashboardStatValue } from "../utils/dashboardViewModels";
import { DashboardErrorState } from "./DashboardErrorState";

interface DashboardStatsSectionProps {
  title: React.ReactNode;
  subtitle: React.ReactNode;
  values: DashboardStatValue[];
  hasData: boolean;
  loading: boolean;
  refreshing: boolean;
  error: string | null;
  onRetry: () => void;
  onRefreshAll: () => void;
}

export const DashboardStatsSection: React.FC<DashboardStatsSectionProps> = ({
  title,
  subtitle,
  values,
  hasData,
  loading,
  refreshing,
  error,
  onRetry,
  onRefreshAll,
}) => {
  const { t } = useTranslation();
  const statLabels = useMemo(() => getDashboardStatLabels(t), [t]);

  const stats: PageHeaderStat[] = useMemo(
    () =>
      values.map((stat, index) => ({
        label: statLabels[index]?.label ?? stat.key,
        value: stat.value,
        icon: statLabels[index]?.icon,
        color: statLabels[index]?.color,
      })),
    [values, statLabels],
  );

  if (error && !hasData) {
    return (
      <div className="mb-4">
        <DashboardErrorState
          message={error}
          onRetry={onRetry}
        />
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title={title}
        subtitle={subtitle}
        stats={hasData ? stats : []}
        loading={loading}
        statLabels={statLabels}
        reverseOrder={true}
        actions={
          <button
            type="button"
            onClick={onRefreshAll}
            disabled={refreshing}
            className="rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-[#004232] transition-opacity hover:opacity-80 disabled:opacity-60 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
          >
            {t("common.refresh")}
          </button>
        }
      />
      {error && hasData ? (
        <div className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700 dark:border-red-700 dark:bg-red-900/20 dark:text-red-300">
          <span>{error}</span>
          <button
            type="button"
            onClick={onRetry}
            className="font-semibold hover:opacity-80"
          >
            {t("ui.retry")}
          </button>
        </div>
      ) : null}
    </>
  );
};
